import { useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, Info, Loader2, MinusCircle, Play, Zap } from "lucide-react";
import { useLocale } from "@ncp/i18n";
import { Badge, Button, cn } from "@ncp/ui";
import type { PageCheckIssue, PageCheckResult, PageScreenshot, PageValidationReport } from "./types";

const CHECKS: Record<string, { en: string; ar: string }> = {
  manifest: { en: "Project and page.json", ar: "المشروع وملف page.json" },
  packages: { en: "Approved packages", ar: "الحزم المعتمدة" },
  capabilities: { en: "Capabilities declared and used", ar: "القدرات المعلنة والمستخدمة" },
  locales: { en: "English and Arabic text", ar: "النصوص العربية والإنجليزية" },
  egress: { en: "No way out besides the platform", ar: "لا منفذ للبيانات خارج المنصة" },
  evals: { en: "Agent evaluation files", ar: "ملفات تقييم الوكلاء" },
  build: { en: "Build", ar: "البناء" },
  render: { en: "Opens in English and Arabic, with an accessibility check", ar: "الفتح بالعربية والإنجليزية مع فحص إمكانية الوصول" },
  agents: { en: "Agent evaluations", ar: "تقييمات الوكلاء" },
};

const STATUS = {
  pass: { icon: CheckCircle2, tone: "text-success-600", badge: "success" as const, en: "Passed", ar: "ناجح" },
  warn: { icon: AlertTriangle, tone: "text-warning-600", badge: "warning" as const, en: "Warnings", ar: "تحذيرات" },
  fail: { icon: AlertCircle, tone: "text-danger-600", badge: "danger" as const, en: "Errors", ar: "أخطاء" },
  skipped: { icon: MinusCircle, tone: "text-muted-foreground", badge: "outline" as const, en: "Skipped", ar: "لم يجر" },
};
const statusOf = (s: string) => STATUS[s as keyof typeof STATUS] ?? STATUS.skipped;

const SEVERITY = {
  error: { icon: AlertCircle, tone: "text-danger-600" },
  warning: { icon: AlertTriangle, tone: "text-warning-600" },
  info: { icon: Info, tone: "text-seablue-600" },
};
const rank = (i: PageCheckIssue) => (i.severity === "error" ? 0 : i.severity === "warning" ? 1 : 2);

function IssueRow({ issue, onOpen }: { issue: PageCheckIssue; onOpen?: (path: string, line?: number | null) => void }) {
  const s = SEVERITY[issue.severity as keyof typeof SEVERITY] ?? SEVERITY.info;
  const Icon = s.icon;
  return (
    <li className="flex items-start gap-2 py-1.5 text-sm">
      <Icon className={cn("mt-0.5 size-3.5 shrink-0", s.tone)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="leading-snug">{issue.message}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="font-mono" dir="ltr">{issue.rule}</span>
          {issue.path ? (
            onOpen ? (
              <button type="button" className="font-mono text-aegold-800 underline-offset-2 hover:underline" dir="ltr" onClick={() => onOpen(issue.path!, issue.line)}>
                {issue.path}{issue.line ? `:${issue.line}` : ""}
              </button>
            ) : <span className="font-mono" dir="ltr">{issue.path}{issue.line ? `:${issue.line}` : ""}</span>
          ) : null}
        </p>
      </div>
    </li>
  );
}

function CheckRow({ check, onOpen }: { check: PageCheckResult; onOpen?: (path: string, line?: number | null) => void }) {
  const { locale } = useLocale();
  const ar = locale === "ar";
  const shown = check.issues.filter((i) => i.severity !== "info" || check.issues.length <= 6);
  const [open, setOpen] = useState(check.status === "fail" || check.status === "warn");
  const s = statusOf(check.status);
  const Icon = s.icon;
  const label = CHECKS[check.key] ? (ar ? CHECKS[check.key].ar : CHECKS[check.key].en) : check.key;
  const sorted = [...(open ? check.issues : shown)].sort((a, b) => rank(a) - rank(b));
  return (
    <li className="rounded-lg border border-sand-200 bg-white/70">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} disabled={check.issues.length === 0}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-start disabled:cursor-default">
        <Icon className={cn("size-4 shrink-0", s.tone)} aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{label}</span>
        {check.issues.length > 0 ? <span className="text-xs text-muted-foreground tabular-nums">{check.issues.length}</span> : null}
        {check.durationMs ? <span className="text-xs text-muted-foreground tabular-nums">{check.durationMs < 1000 ? `${check.durationMs} ms` : `${(check.durationMs / 1000).toFixed(1)} s`}</span> : null}
        {check.issues.length > 0 ? <ChevronDown className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-180")} aria-hidden /> : null}
      </button>
      {open && sorted.length > 0 ? (
        <ul className="border-t border-sand-100 px-3 pb-1.5">
          {sorted.map((i, n) => <IssueRow key={`${i.rule}-${n}`} issue={i} onOpen={onOpen} />)}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * A page version's validation report: each check with its findings (a path opens the file), the screenshots of the headless render in
 * English and Arabic, and buttons to run the quick or the full checks again.
 */
export function ValidationReport({ report, stale, screenshots, running, onRun, onOpenIssue, className }: {
  report: PageValidationReport | null | undefined;
  /** The files changed since the report was made. */
  stale?: boolean;
  screenshots?: Partial<Record<"en" | "ar", PageScreenshot>> | null;
  running?: "quick" | "full" | null;
  onRun?: (mode: "quick" | "full") => void;
  onOpenIssue?: (path: string, line?: number | null) => void;
  className?: string;
}) {
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [zoom, setZoom] = useState<"en" | "ar" | null>(null);
  const s = report ? statusOf(report.status) : null;
  const when = report ? new Date(report.at).toLocaleString(ar ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : null;
  return (
    <section className={cn("space-y-3", className)} aria-label={ar ? "نتيجة التحقق" : "Validation"}>
      <div className="flex flex-wrap items-center gap-2">
        {report && s ? (
          <>
            <Badge variant={s.badge} dot>{ar ? s.ar : s.en}</Badge>
            <span className="text-sm text-muted-foreground">
              {ar ? `${report.errors} خطأ، ${report.warnings} تحذير` : `${report.errors} error${report.errors === 1 ? "" : "s"}, ${report.warnings} warning${report.warnings === 1 ? "" : "s"}`}
              {" · "}{report.mode === "full" ? (ar ? "فحص كامل" : "full check") : (ar ? "فحص سريع" : "quick check")}{" · "}{when}
            </span>
          </>
        ) : <span className="text-sm text-muted-foreground">{ar ? "لم تفحص هذه النسخة بعد." : "This version has not been checked yet."}</span>}
        {onRun ? (
          <span className="ms-auto flex gap-1.5">
            <Button size="sm" variant="outline" disabled={!!running} onClick={() => onRun("quick")}>
              {running === "quick" ? <Loader2 className="size-3.5 animate-spin" /> : <Zap className="size-3.5" />}{ar ? "فحص سريع" : "Quick check"}
            </Button>
            <Button size="sm" disabled={!!running} onClick={() => onRun("full")}>
              {running === "full" ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}{ar ? "فحص كامل" : "Full check"}
            </Button>
          </span>
        ) : null}
      </div>
      {stale ? (
        <p className="rounded-lg border border-warning-100 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          {ar ? "تغيرت الملفات بعد هذا الفحص؛ أعد الفحص قبل الإصدار." : "The files changed after this check; check again before releasing."}
        </p>
      ) : null}
      {report ? <ul className="space-y-1.5">{report.checks.map((c) => <CheckRow key={c.key} check={c} onOpen={onOpenIssue} />)}</ul> : null}
      {screenshots && (screenshots.en || screenshots.ar) ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {(["en", "ar"] as const).map((l) => screenshots[l] ? (
            <figure key={l} className={cn("overflow-hidden rounded-lg border border-sand-200 bg-white", zoom === l && "sm:col-span-2")}>
              <button type="button" className="block w-full" onClick={() => setZoom((z) => (z === l ? null : l))} aria-label={ar ? "تكبير" : "Enlarge"}>
                <img src={screenshots[l]!.url} alt={l === "en" ? (ar ? "الصفحة بالإنجليزية" : "The page in English") : (ar ? "الصفحة بالعربية" : "The page in Arabic")} className="block w-full" loading="lazy" />
              </button>
              <figcaption className="border-t border-sand-100 px-3 py-1.5 text-xs text-muted-foreground">{l === "en" ? "English" : "العربية"}</figcaption>
            </figure>
          ) : null)}
        </div>
      ) : null}
    </section>
  );
}
